import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Plus, Trash2, Upload, Save, CreditCard, CheckCircle2, AlertCircle, CalendarClock } from "lucide-react";
import api from "../lib/apiClient";
import { AuthImage } from "../components/AuthImage";
import { toast } from "sonner";

const VIS = [
  ["DISCOVERABLE", "Découvrable"],
  ["OPEN_TO_COLLABORATION", "Ouvert aux collaborations"],
  ["OPEN_TO_MANAGEMENT", "Ouvert au management"],
  ["PRIVATE", "Privé"],
];

export default function ProfessionalSetup() {
  const [cats, setCats] = useState([]);
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [portfolio, setPortfolio] = useState([]);

  useEffect(() => {
    api.get("/marketplace/categories").then((r) => setCats(r.data.categories || [])).catch(() => {});
    api.get("/marketplace/profile/me").then((r) => {
      const p = r.data.profile || {};
      setF({
        headline: p.headline || "", bio: p.bio || "", categories: p.categories || [],
        specialties: (p.specialties || []).join(", "), services: p.services || [],
        pricing_note: p.pricing_note || "", city: p.city || "", country: p.country || "",
        zone: p.zone || "", availability: p.availability || "", experience_years: p.experience_years ?? "",
        visibility: p.visibility || "DISCOVERABLE",
      });
      setPortfolio(p.portfolio || []);
    }).catch(() => setF({ categories: [], services: [], specialties: "", visibility: "DISCOVERABLE" }));
  }, []);

  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const toggleCat = (key) => setF((s) => ({ ...s, categories: s.categories.includes(key) ? s.categories.filter((c) => c !== key) : [...s.categories, key] }));
  const addService = () => setF((s) => ({ ...s, services: [...s.services, { title: "", description: "", price: "", unit: "" }] }));
  const setService = (i, k, v) => setF((s) => ({ ...s, services: s.services.map((x, j) => j === i ? { ...x, [k]: v } : x) }));
  const rmService = (i) => setF((s) => ({ ...s, services: s.services.filter((_, j) => j !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/marketplace/profile", {
        ...f,
        specialties: f.specialties.split(",").map((x) => x.trim()).filter(Boolean),
        experience_years: f.experience_years === "" ? null : Number(f.experience_years),
        services: f.services.filter((x) => x.title),
      });
      toast.success("Profil professionnel enregistré");
    } catch { toast.error("Échec de l'enregistrement"); }
    finally { setSaving(false); }
  };

  const uploadPortfolio = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try { const { data } = await api.post("/marketplace/profile/portfolio", { image_base64: reader.result }); setPortfolio((p) => [...p, data]); toast.success("Image ajoutée"); }
      catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
    };
    reader.readAsDataURL(file);
  };
  const rmPortfolio = async (id) => { try { await api.delete(`/marketplace/profile/portfolio/${id}`); setPortfolio((p) => p.filter((x) => x.id !== id)); } catch {} };

  if (!f) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="pro-setup">
      <div>
        <p className="text-sm font-mono uppercase tracking-wider text-amber-500">Marché Cehnura</p>
        <h1 className="font-display text-3xl font-extrabold mt-1">Mon profil professionnel</h1>
        <p className="text-zinc-500 mt-2">Propose tes services et sois trouvé par les porteurs de projet.</p>
      </div>

      <ConnectCard />
      <AvailabilityCard />

      <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-6 space-y-4">
        <TInput label="Titre / accroche" v={f.headline} onC={set("headline")} tid="pro-headline" placeholder="Ex : Réalisateur & monteur clips musicaux" />
        <TArea label="Bio" v={f.bio} onC={set("bio")} tid="pro-bio" />
        <div>
          <label className="block text-xs font-medium text-zinc-400 mb-1.5">Catégories</label>
          <div className="flex flex-wrap gap-2">
            {cats.map((c) => (
              <button key={c.key} onClick={() => toggleCat(c.key)} data-testid={`pro-cat-${c.key}`}
                className={`px-3 h-9 rounded-lg text-xs border ${f.categories.includes(c.key) ? "bg-amber-500/15 text-amber-400 border-amber-500/40" : "border-zinc-800 text-zinc-400"}`}>{c.label}</button>
            ))}
          </div>
        </div>
        <TInput label="Spécialités (séparées par des virgules)" v={f.specialties} onC={set("specialties")} tid="pro-specialties" placeholder="Montage, étalonnage, motion" />
        <div className="grid sm:grid-cols-2 gap-4">
          <TInput label="Ville" v={f.city} onC={set("city")} tid="pro-city" />
          <TInput label="Pays" v={f.country} onC={set("country")} tid="pro-country" />
          <TInput label="Zone d'intervention" v={f.zone} onC={set("zone")} tid="pro-zone" placeholder="Île-de-France, à distance…" />
          <TInput label="Disponibilité" v={f.availability} onC={set("availability")} tid="pro-availability" placeholder="Disponible / sur devis…" />
          <TInput label="Années d'expérience" v={f.experience_years} onC={set("experience_years")} tid="pro-exp" type="number" />
          <div>
            <label className="block text-xs font-medium text-zinc-400 mb-1.5">Visibilité</label>
            <select value={f.visibility} onChange={(e) => set("visibility")(e.target.value)} data-testid="pro-visibility"
              className="w-full h-11 px-3 bg-zinc-900 border border-zinc-800 rounded-xl text-sm text-zinc-200">
              {VIS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-6 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-bold">Services & tarifs</h2>
          <button onClick={addService} data-testid="pro-add-service" className="text-sm text-amber-400 inline-flex items-center gap-1"><Plus className="w-4 h-4" />Ajouter</button>
        </div>
        {f.services.length === 0 && <p className="text-sm text-zinc-500">Aucun service. Ajoute ce que tu proposes.</p>}
        {f.services.map((s, i) => (
          <div key={i} className="grid sm:grid-cols-[1fr_1fr_100px_80px_auto] gap-2 items-center" data-testid={`pro-service-${i}`}>
            <input value={s.title} onChange={(e) => setService(i, "title", e.target.value)} placeholder="Service" className="h-10 px-3 bg-zinc-900 border border-zinc-800 rounded-lg text-sm text-zinc-100" />
            <input value={s.description} onChange={(e) => setService(i, "description", e.target.value)} placeholder="Détail" className="h-10 px-3 bg-zinc-900 border border-zinc-800 rounded-lg text-sm text-zinc-100" />
            <input value={s.price} onChange={(e) => setService(i, "price", e.target.value)} placeholder="Prix €" className="h-10 px-3 bg-zinc-900 border border-zinc-800 rounded-lg text-sm text-zinc-100" />
            <input value={s.unit} onChange={(e) => setService(i, "unit", e.target.value)} placeholder="unité" className="h-10 px-3 bg-zinc-900 border border-zinc-800 rounded-lg text-sm text-zinc-100" />
            <button onClick={() => rmService(i)} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <TInput label="Note sur les tarifs" v={f.pricing_note} onC={set("pricing_note")} tid="pro-pricing-note" placeholder="Sur devis selon le projet" />
      </div>

      <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-6 space-y-3">
        <h2 className="font-display text-lg font-bold">Portfolio</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {portfolio.map((it) => (
            <div key={it.id} className="relative rounded-xl overflow-hidden border border-zinc-800 group">
              <AuthImage path={it.media_path} className="w-full h-28 object-cover" />
              <button onClick={() => rmPortfolio(it.id)} data-testid={`pf-del-${it.id}`} className="absolute top-1 right-1 w-7 h-7 rounded-lg bg-black/70 text-red-400 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <label className="h-28 rounded-xl border border-dashed border-zinc-700 flex flex-col items-center justify-center text-zinc-500 text-xs cursor-pointer hover:border-amber-500/50" data-testid="pf-upload">
            <Upload className="w-5 h-5 mb-1 text-amber-400" />Ajouter
            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPortfolio(e.target.files?.[0])} />
          </label>
        </div>
      </div>

      <button onClick={save} disabled={saving} data-testid="pro-save" className="h-12 px-6 rounded-2xl gold-gradient text-black font-semibold inline-flex items-center gap-2 disabled:opacity-60">
        {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}Enregistrer mon profil
      </button>
    </div>
  );
}

function AvailabilityCard() {
  const [slots, setSlots] = useState([]);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const load = () => api.get("/marketplace/availability/me").then((r) => setSlots(r.data.slots || [])).catch(() => setSlots([]));
  useEffect(() => { load(); }, []); // eslint-disable-line
  const add = async () => {
    if (!start || !end) { toast.error("Renseigne le début et la fin"); return; }
    try { await api.post("/marketplace/availability", { start: new Date(start).toISOString(), end: new Date(end).toISOString() }); toast.success("Créneau ajouté"); setStart(""); setEnd(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
  };
  const rm = async (id) => { try { await api.delete(`/marketplace/availability/${id}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Échec"); } };
  const fmt = (s) => s.slice(0, 16).replace("T", " ");
  return (
    <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-6" data-testid="availability-card">
      <div className="flex items-center gap-2 mb-1"><CalendarClock className="w-5 h-5 text-amber-400" /><h2 className="font-display text-lg font-bold">Mes disponibilités</h2></div>
      <p className="text-sm text-zinc-500 mb-3">Ajoute des créneaux : tes clients pourront en réserver un lorsqu'ils acceptent ton devis (anti double-réservation garanti).</p>
      <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
        <div><label className="block text-xs text-zinc-400 mb-1">Début</label><input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} data-testid="slot-start" className="w-full h-11 px-3 bg-zinc-900 border border-zinc-800 rounded-xl text-sm text-zinc-100" /></div>
        <div><label className="block text-xs text-zinc-400 mb-1">Fin</label><input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="slot-end" className="w-full h-11 px-3 bg-zinc-900 border border-zinc-800 rounded-xl text-sm text-zinc-100" /></div>
        <button onClick={add} data-testid="slot-add" className="h-11 px-4 rounded-xl gold-gradient text-black font-semibold text-sm inline-flex items-center gap-1"><Plus className="w-4 h-4" />Ajouter</button>
      </div>
      <div className="mt-4 space-y-2">
        {slots.length === 0 ? <p className="text-sm text-zinc-500">Aucun créneau pour l'instant.</p> : slots.map((s) => (
          <div key={s.id} className="flex items-center justify-between gap-2 border border-zinc-800 rounded-xl px-3 py-2" data-testid={`slot-${s.id}`}>
            <span className="text-sm text-zinc-200 inline-flex items-center gap-2"><CalendarClock className="w-4 h-4 text-zinc-500" />{fmt(s.start)} → {fmt(s.end)}</span>
            <span className="flex items-center gap-2">
              <span className={`text-[11px] px-2 py-0.5 rounded-full border ${s.status === "BOOKED" ? "text-violet-400 border-violet-500/40 bg-violet-500/10" : "text-emerald-400 border-emerald-500/30 bg-emerald-500/10"}`}>{s.status === "BOOKED" ? "Réservé" : "Libre"}</span>
              {s.status !== "BOOKED" && <button onClick={() => rm(s.id)} data-testid={`slot-del-${s.id}`} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ConnectCard() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/marketplace/connect/status").then((r) => setStatus(r.data)).catch(() => setStatus({ connected: false }));
  useEffect(() => {
    load();
    if (params.get("connect") === "done") toast.success("Configuration Stripe mise à jour.");
  }, []); // eslint-disable-line

  const onboard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/marketplace/connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de la configuration Stripe"); setBusy(false); }
  };

  const active = status?.charges_enabled && status?.payouts_enabled;
  return (
    <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-6" data-testid="connect-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shrink-0">
          <CreditCard className="w-5 h-5 text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-lg font-bold">Paiements & versements</h2>
          <p className="text-sm text-zinc-500 mt-1">Configure ton compte Stripe pour recevoir tes paiements en toute sécurité. Cehnura sécurise les fonds et prélève une commission de 10 % à la livraison validée.</p>
          <div className="mt-3">
            {status === null ? (
              <span className="text-xs text-zinc-500 inline-flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" />Chargement…</span>
            ) : active ? (
              <span className="text-sm text-emerald-400 inline-flex items-center gap-1.5" data-testid="connect-active"><CheckCircle2 className="w-4 h-4" />Compte actif — tu peux recevoir des paiements.</span>
            ) : status.connected ? (
              <div className="space-y-2" data-testid="connect-pending">
                <span className="text-sm text-amber-400 inline-flex items-center gap-1.5"><AlertCircle className="w-4 h-4" />Configuration incomplète.</span>
                <button onClick={onboard} disabled={busy} data-testid="connect-continue-btn" className="block h-10 px-5 rounded-xl gold-gradient text-black font-semibold text-sm disabled:opacity-60">{busy ? "Redirection…" : "Terminer ma configuration"}</button>
              </div>
            ) : (
              <button onClick={onboard} disabled={busy} data-testid="connect-onboard-btn" className="h-10 px-5 rounded-xl gold-gradient text-black font-semibold text-sm disabled:opacity-60">{busy ? "Redirection…" : "Configurer mes paiements"}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function TInput({ label, v, onC, tid, placeholder, type = "text" }) {
  return (
    <div>
      <label className="block text-xs font-medium text-zinc-400 mb-1.5">{label}</label>
      <input type={type} value={v} onChange={(e) => onC(e.target.value)} data-testid={tid} placeholder={placeholder}
        className="w-full h-11 px-4 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 placeholder-zinc-600 outline-none focus:border-amber-500" />
    </div>
  );
}
function TArea({ label, v, onC, tid }) {
  return (
    <div>
      <label className="block text-xs font-medium text-zinc-400 mb-1.5">{label}</label>
      <textarea value={v} onChange={(e) => onC(e.target.value)} data-testid={tid} rows={3}
        className="w-full px-4 py-3 rounded-xl bg-zinc-900 border border-zinc-800 text-zinc-100 placeholder-zinc-600 outline-none focus:border-amber-500 resize-none" />
    </div>
  );
}
